import { computed, ref, watch } from 'vue';
import useMetrics from '@/composables/v2/metrics/useMetrics';
import { createSessionEvent } from '@/models/Metrics/SessionEvent';
import SessionEventScope from '@/models/Metrics/SessionEventScope';
import SessionEventType from '@/models/Metrics/SessionEventType';
import { useFullscreen } from '@vueuse/core';
import isAttentionLostFor from '@/utils/attentionLost';

const { dispatchSessionEvents, flushSessionEventsIfCurrentSessionExist } = useMetrics();

const { isFullscreen, enter: enterFullscreen } = useFullscreen();

const isFocused = ref(true);
const isVisible = ref(document.visibilityState === 'visible');
const isDistracted = computed(() => !isFocused.value || !isVisible.value);
const isUnloadProtected = ref(false);
const isActive = ref(false);
const isListenersRegistered = ref(false);
// eprocom-pwa#857: a recording stops, and never starts, while this is true.
const isAttentionLost = computed(() =>
    isAttentionLostFor({ active: isActive.value, distracted: isDistracted.value, fullscreen: isFullscreen.value })
);

watch(isFullscreen, () => {
    if (isFullscreen.value) {
        dispatchSessionEvents([createSessionEvent(SessionEventScope.SESSION, SessionEventType.FULLSCREEN_IN)]);
    }
    if (!isFullscreen.value) {
        dispatchSessionEvents([createSessionEvent(SessionEventScope.SESSION, SessionEventType.FULLSCREEN_OUT)]);
    }
});

export default function useUserAttention() {
    function handleFocus() {
        isFocused.value = true;
        dispatchSessionEvents([
            createSessionEvent(SessionEventScope.SESSION, SessionEventType.FOCUS_IN, undefined, undefined, 'screen'),
        ]);
    }

    function handleBlur() {
        isFocused.value = false;
        dispatchSessionEvents([
            createSessionEvent(SessionEventScope.SESSION, SessionEventType.FOCUS_OUT, undefined, undefined, 'screen'),
        ]);
    }

    function handleVisibilityChange() {
        isVisible.value = document.visibilityState === 'visible';
    }

    function enableUnloadProtection() {
        isUnloadProtected.value = true;
    }
    function disableUnloadProtection() {
        isUnloadProtected.value = false;
    }

    function handleBeforeUnload(event) {
        if (isUnloadProtected.value) {
            event.preventDefault();
            // eslint-disable-next-line no-param-reassign
            event.returnValue = 'Attention! Vous allez perdre votre progression !';
            dispatchSessionEvents([createSessionEvent(SessionEventScope.SESSION, SessionEventType.PAGE_REFRESH)]);
            return 'Attention! Vous allez perdre votre progression !';
        }
        return '';
    }

    function registerListeners() {
        if (isListenersRegistered.value) return;
        isFocused.value = document.hasFocus();
        isVisible.value = document.visibilityState === 'visible';
        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleBlur);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);
        isListenersRegistered.value = true;
    }
    function unregisterListeners() {
        if (!isListenersRegistered.value) return;
        window.removeEventListener('focus', handleFocus);
        window.removeEventListener('blur', handleBlur);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        isListenersRegistered.value = false;
    }

    function setActive(active) {
        isActive.value = active;
        if (active) {
            registerListeners();
        } else {
            unregisterListeners();
        }
    }

    watch(isDistracted, async () => {
        if (isActive.value) {
            await flushSessionEventsIfCurrentSessionExist();
        }
    });

    return {
        isFullscreen,
        isFocused,
        isVisible,
        isDistracted,
        isAttentionLost,
        enterFullscreen,
        enableUnloadProtection,
        disableUnloadProtection,
        setActive,
    };
}
